import type { PriceInterval } from '../types';

export interface PeriodPrice {
  interval: PriceInterval;
  amount_minor: number;
  currency: string;
}

export interface Saving {
  /** Saved against paying monthly for the same months, in minor units. */
  amount_minor: number;
  /** Rounded to a whole percent. */
  percent: number;
  /** Set when the saving is exactly a whole number of monthly payments. */
  months_free: number | null;
}

export const INTERVAL_MONTHS: Record<PriceInterval, number> = { monthly: 1, quarterly: 3, yearly: 12 };
const ORDER: PriceInterval[] = ['monthly', 'quarterly', 'yearly'];

/** The periods to offer, in a fixed order, one per interval the plan really has. */
export function periodsToOffer(prices: PeriodPrice[]): PeriodPrice[] {
  return ORDER.map((iv) => prices.find((p) => p.interval === iv)).filter((p): p is PeriodPrice => !!p);
}

/** What `price` saves against `monthly x months`; null when it saves nothing or there is no monthly price. */
export function savingFor(price: PeriodPrice, prices: PeriodPrice[]): Saving | null {
  const monthly = prices.find((p) => p.interval === 'monthly' && p.currency === price.currency);
  if (!monthly || price.interval === 'monthly') return null;
  const full = monthly.amount_minor * INTERVAL_MONTHS[price.interval];
  const saved = full - price.amount_minor;
  if (saved <= 0 || full <= 0) return null;
  return {
    amount_minor: saved,
    percent: Math.round((saved / full) * 100),
    months_free: monthly.amount_minor > 0 && saved % monthly.amount_minor === 0 ? saved / monthly.amount_minor : null,
  };
}
